import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Button, Box } from '@material-ui/core';
import { AddBox, ArrowForward, Delete, Edit } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';

import { TitleContainer, ContentContainer, Cards, ActionsBox, DialogWrap, AccessControl } from 'src/components';
import { useProjectApi } from 'src/services';
import { ProjectCreateDialog, ProjectDeleteDialog, ProjectEditDialog } from 'src/scenes/CompanySummary/components';
import { useFetchStatus } from 'src/hooks';
import { ProjectCard } from 'src/scenes/Projects/components/ProjectCard';


const useStyles = makeStyles(theme => ({
  mainContainer: {
    width: 'calc(100%)',
  },
  content: {
    maxWidth: theme.spacing(140),
  },
  defaultItem: {
    padding: theme.spacing(1),
  },
  cardsBox: {
    width: 800,
  },
}));

const SitesContainer = props => {

  const classes = useStyles();
  const history = useHistory();
  const { search } = useLocation();
  const match = useRouteMatch();

  const { projectId } = match.params;

  const projectApi = useProjectApi();
  const [ projects, setProjects ] = useState([]);

  const getProjects = async () => {
    const { projectList } = await projectApi.getProjects({ folderId: projectId });
    setProjects(projectList[0]?.sites || []);
  };

  const { handleConfirm: fetchProjects } = useFetchStatus({
    confirmFn: getProjects,
  });

  useEffect(() => {
    fetchProjects();
  }, [ search ]); // eslint-disable-line

  const [ dialog, setDialog ] = useState({ isOpen: false, type: '' });
  const closeDialog = () => setDialog({ isOpen: false, type: '' });
  const openDialog = (type, data = {}) => setDialog({ isOpen: true, type, data });

  const openCreateProjectDialog = () => openDialog('create-project');
  const openDeleteProjectDialog = data => openDialog('delete-project', data);
  const openEditProjectDialog = data => openDialog('edit-project', data);

  const getDialogType = () => {
    switch (dialog.type) {
      case 'create-project':
        return <ProjectCreateDialog actions={{ closeDialog, createProject }} parentId={projectId} />;
      case 'delete-project':
        return <ProjectDeleteDialog project={dialog.data} actions={{ closeDialog, deleteProject }} parentId={projectId} />;
      case 'edit-project':
        return <ProjectEditDialog project={dialog.data} actions={{ closeDialog, editProject }} parentId={projectId} />;
      default:
        break;
    }
  };

  const goToSite = site => history.push(`/web/projects/${projectId}/sites/${site.id}/land`);

  const cardActions = useCallback(itemData => ([
    {
      Icon: Edit,
      tooltip: 'Editar',
      onClick: () => openEditProjectDialog(itemData),
      accessPermission: 'project:edit',
    },
    {
      Icon: Delete,
      tooltip: 'Eliminar',
      onClick: () => openDeleteProjectDialog(itemData),
      accessPermission: 'project:delete',
    },
    {
      Icon: ArrowForward,
      tooltip: 'Ver sitio',
      onClick: () => goToSite(itemData),
    },
  ]), []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderSitesCard = useCallback(({ itemData }) => (
    <ProjectCard {...itemData} isSite />
  ), []);

  const createProject = async newProject => {
    const [ createdProject ] = await projectApi.createProject({ ...newProject, parentId: projectId });
    setProjects(prevProjects => [
      createdProject,
      ...prevProjects,
    ]);
  };

  const deleteProject = async projectId => {
    await projectApi.deleteProject(projectId);
    fetchProjects();
  };

  const editProject = async editProject => {
    await projectApi.updateProject({ projectId: editProject.id, name: editProject.name });
    setProjects(projects.map(project => editProject.id === project.id ? editProject : project));
  };

  return (
    <Grid container justifyContent="center" className={classes.mainContainer}>
      <TitleContainer
        project={props.project}
        breadcrumbs={[ 'projects', 'project', { name: 'Sitios' } ]}>
        Sitios
      </TitleContainer>
      <ContentContainer maxWidth="xl" className={classes.content}>
        <AccessControl action='project:create'>
          <ActionsBox>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={openCreateProjectDialog}
              startIcon={<AddBox fontSize="small" />}
            >
              Crear sitio
            </Button>
          </ActionsBox>
        </AccessControl>
        <Box display="flex" alignItems="center" flexDirection="column">
          <Box className={classes.cardsBox}>
            <Cards
              classes={{ defaultItem: classes.defaultItem }}
              values={projects}
              actions={cardActions}
              Content={renderSitesCard}
            />
          </Box>
        </Box>
      </ContentContainer>
      <DialogWrap maxWidth='sm' fullWidth onClose={closeDialog} aria-labelledby='form-dialog-title' open={dialog.isOpen}>
        {getDialogType()}
      </DialogWrap>
    </Grid>
  );
};

SitesContainer.propTypes = {
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export {
  SitesContainer,
};
